import { useMemo, useState } from 'react'
import EventsLayout from '@/components/layouts/EventLayout'
import { Input, Select, Form, Tag, message, Space } from 'antd'
import { EyeOutlined, SendOutlined } from '@ant-design/icons'
import { HiOutlinePencil, HiOutlineUser } from 'react-icons/hi'
import { Button } from '@/components/ui'
import { PageHeader } from '@/components/patterns'
import { usePage, router, useForm } from '@inertiajs/react'
import { api } from '@/lib/apiClient'
import { RecipientSelectionDrawer } from '../components/recipient_selection/RecipientSelectionDrawer'
import FixedBottomActions from '@/components/layouts/events/FixedBottomActions'
import {
  defaultRecipientFilters,
  type Recipient,
  type RecipientFilters,
  type RecipientFilterOptions,
  type RecipientSelectionResult,
} from '../components/recipient_selection/types'
import EmailAttachmentsField from './components/EmailAttachmentsField'
import SendTestEmailModal from './components/SendTestEmailModal'
import EmailPreviewModal from './components/EmailPreviewModal'
import EmailEditorModal from './components/EmailEditorModal'
import LoadEmailContentModal, {
  type ImportedEmailContent,
} from './components/LoadEmailContentModal'
import {
  defaultEmailHtmlContent,
  defaultEmailDesignJson,
  DEFAULT_ATTACHMENT_LIMITS,
  type AttachmentLimits,
  type EmailAttachment,
  type Event,
  type EmailFormData,
} from './types'
import { type MergeTagOptions } from '@/lib/mergeTags'
import '@/styles/compact-form.css'

type PageProps = {
  event: Event
  formMode?: 'create' | 'edit'
  initialEmail?: Partial<EmailFormData>
  availableRecipients?: Recipient[]
  initialRecipientFilters?: RecipientFilters
  filterOptions?: RecipientFilterOptions
  errors?: Record<string, string | string[]>
  mergeTagOptions?: MergeTagOptions
  attachmentLimits?: AttachmentLimits
}

type FormValues = {
  campaignName: string
  subjectLine: string
  senderEmail: string
  senderEmailDomain: string
  senderName: string
  replyToEmail?: string
}

type PreviewRecipient = {
  id?: string
  name: string
  email: string
  source?: 'selected' | 'recipient' | 'latest' | 'sample'
}

type EmailPreviewData = {
  campaignName: string
  subjectLine: string
  senderName: string
  senderEmail: string
  recipientCount: number
  htmlContent: string
  updatedAt?: string
}

type PreviewResponse = {
  html: string
  subject: string
  attendee?: PreviewRecipient
}

type SendTestResponse = {
  sent_count: number
  attendee?: PreviewRecipient
}

const EMAIL_DOMAINS = [
  { label: '@avaeventhub.com', value: '@avaeventhub.com' }
]

const EMPTY_FILTER_OPTIONS: RecipientFilterOptions = { fields: [], sessionChoices: [] }

const NewEmailBlast = () => {
  const {
    event,
    formMode = 'create',
    initialEmail,
    availableRecipients = [],
    initialRecipientFilters,
    filterOptions = EMPTY_FILTER_OPTIONS,
    mergeTagOptions,
    attachmentLimits = DEFAULT_ATTACHMENT_LIMITS,
  } = usePage<PageProps>().props
  const [form] = Form.useForm()

  const initialSenderParts = useMemo(() => {
    const full = initialEmail?.senderEmail || ''
    const [local, domain] = full.split('@', 2)
    const candidateDomain = domain ? `@${domain}` : EMAIL_DOMAINS[0].value
    const resolvedDomain = EMAIL_DOMAINS.some((d) => d.value === candidateDomain)
      ? candidateDomain
      : EMAIL_DOMAINS[0].value
    return {
      local: local || '',
      domain: resolvedDomain,
    }
  }, [initialEmail?.senderEmail])

  const initialValues = useMemo(
    () => ({
      campaignName: initialEmail?.campaignName || '',
      subjectLine: initialEmail?.subjectLine || '',
      senderEmail: initialSenderParts.local,
      senderEmailDomain: initialSenderParts.domain,
      senderName: initialEmail?.senderName || '',
      replyToEmail: initialEmail?.replyToEmail || '',
    }),
    [initialEmail, initialSenderParts]
  )

  const initialHtml = useMemo(
    () =>
      initialEmail?.htmlContent && initialEmail.htmlContent.trim().length > 0
        ? initialEmail.htmlContent
        : defaultEmailHtmlContent,
    [initialEmail?.htmlContent]
  )

  const initialDesign = useMemo(
    () =>
      initialEmail?.designJson && Object.keys(initialEmail.designJson).length > 0
        ? initialEmail.designJson
        : defaultEmailDesignJson,
    [initialEmail?.designJson]
  )

  // Form state
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>(
    initialEmail?.recipientIds || []
  )
  const [recipientFilters, setRecipientFilters] = useState<RecipientFilters>(
    initialRecipientFilters || initialEmail?.recipientFilters || defaultRecipientFilters
  )
  const [addToCalendar] = useState(initialEmail?.addToCalendar || false)
  const [htmlContent, setHtmlContent] = useState(initialHtml)
  const [designJson, setDesignJson] = useState<Record<string, unknown>>(initialDesign)
  const [attachments, setAttachments] = useState<EmailAttachment[]>(
    initialEmail?.attachments || []
  )

  // The email body counts towards the same size limit as the files do.
  const htmlByteSize = useMemo(() => new TextEncoder().encode(htmlContent).length, [htmlContent])

  const emailBlastForm = useForm({
    email_blast: {
      campaign_name: initialValues.campaignName,
      subject_line: initialValues.subjectLine,
      sender_email: `${initialValues.senderEmail}${initialValues.senderEmailDomain}`,
      sender_name: initialValues.senderName,
      reply_to_email: initialValues.replyToEmail || '',
      add_to_calendar: addToCalendar,
      recipient_ids: initialEmail?.recipientIds || [],
      recipient_filters:
        initialRecipientFilters || initialEmail?.recipientFilters || defaultRecipientFilters,
      html_content: initialHtml,
      design_json: initialDesign,
      attachment_signed_ids: (initialEmail?.attachments || []).map((file) => file.signedId),
      status: (initialEmail as { status?: string })?.status || 'draft',
    },
  })

  // Modal states
  const [recipientDrawerOpen, setRecipientDrawerOpen] = useState(false)
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [editorModalOpen, setEditorModalOpen] = useState(false)
  const [loadContentModalOpen, setLoadContentModalOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewSubject, setPreviewSubject] = useState<string | null>(null)
  const [previewRecipient, setPreviewRecipient] = useState<PreviewRecipient | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [testSendLoading, setTestSendLoading] = useState(false)

  const handleSelectRecipients = () => {
    setRecipientDrawerOpen(true)
  }

  const handleApplyRecipients = (result: RecipientSelectionResult) => {
    setSelectedRecipientIds(result.recipientIds)
    setRecipientFilters(result.filters)
    emailBlastForm.setData('email_blast', {
      ...(emailBlastForm.data.email_blast || {}),
      recipient_ids: result.recipientIds,
      recipient_filters: result.filters,
    })
    setRecipientDrawerOpen(false)
  }

  const handleAttachmentsChange = (nextAttachments: EmailAttachment[]) => {
    setAttachments(nextAttachments)
    emailBlastForm.setData('email_blast', {
      ...(emailBlastForm.data.email_blast || {}),
      attachment_signed_ids: nextAttachments.map((file) => file.signedId),
    })
  }

  const handleEditEmail = () => {
    setEditorModalOpen(true)
  }

  const handleSaveEmailContent = (newHtml: string, newDesignJson: Record<string, unknown>) => {
    setHtmlContent(newHtml)
    setDesignJson(newDesignJson)
    emailBlastForm.setData('email_blast', {
      ...(emailBlastForm.data.email_blast || {}),
      html_content: newHtml,
      design_json: newDesignJson,
    })
    setEditorModalOpen(false)
    message.success('Email content saved')
  }

  const handleLoadContentFromEvent = (content: ImportedEmailContent) => {
    const nextSubjectLine = content.subjectLine || ''
    const nextHtmlContent = content.htmlContent || defaultEmailHtmlContent
    const nextDesignJson = content.designJson || defaultEmailDesignJson

    form.setFieldValue('subjectLine', nextSubjectLine)
    setHtmlContent(nextHtmlContent)
    setDesignJson(nextDesignJson)
    emailBlastForm.setData('email_blast', {
      ...(emailBlastForm.data.email_blast || {}),
      subject_line: nextSubjectLine,
      html_content: nextHtmlContent,
      design_json: nextDesignJson,
    })
    message.success(`Loaded content from "${content.sourceEventName}"`)
  }

  const handlePreview = async () => {
    if (!htmlContent || !htmlContent.trim()) {
      message.error('Email content cannot be empty')
      return
    }

    const values = form.getFieldsValue() as FormValues
    setPreviewLoading(true)
    try {
      const response = await api.post<PreviewResponse>(`/events/${event.slug}/messaging/email_blasts/preview`, {
        subject_line: values.subjectLine || '',
        html_content: htmlContent,
        recipient_ids: selectedRecipientIds,
        preview_registrant_id: selectedRecipientIds[0],
      })

      setPreviewHtml(response.html || htmlContent)
      setPreviewSubject(response.subject || values.subjectLine || '')
      setPreviewRecipient(response.attendee || null)
      setPreviewModalOpen(true)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to load preview.')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSendTest = () => {
    setTestEmailModalOpen(true)
  }

  const handleSendTestEmails = async (emails: string[]) => {
    if (!htmlContent || !htmlContent.trim()) {
      message.error('Email content cannot be empty')
      return
    }

    const values = form.getFieldsValue() as FormValues
    const senderEmailFull = `${values.senderEmail || ''}${values.senderEmailDomain || ''}`.trim()

    if (!values.subjectLine?.trim()) {
      message.error('Please enter subject line')
      return
    }

    if (!senderEmailFull) {
      message.error('Please enter sender email')
      return
    }

    if (!values.senderName?.trim()) {
      message.error('Please enter sender name')
      return
    }

    setTestSendLoading(true)
    try {
      const response = await api.post<SendTestResponse>(`/events/${event.slug}/messaging/email_blasts/send_test`, {
        test_emails: emails,
        subject_line: values.subjectLine || '',
        html_content: htmlContent,
        sender_email: senderEmailFull,
        sender_name: values.senderName || '',
        reply_to_email: values.replyToEmail || '',
        recipient_ids: selectedRecipientIds,
        preview_registrant_id: selectedRecipientIds[0],
        attachment_signed_ids: attachments.map((file) => file.signedId),
      })

      const count = response.sent_count ?? emails.length
      const attendee = response.attendee
      const suffix = attendee?.source === 'sample' ? ' (using sample attendee data)' : ''
      message.success(
        `Test email has been sent to ${count} recipient${count !== 1 ? 's' : ''}.${suffix}`
      )
      setTestEmailModalOpen(false)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to send test email.')
    } finally {
      setTestSendLoading(false)
    }
  }

  const handleSubmit = (values: FormValues) => {
    const senderEmailFull = `${values.senderEmail || ''}${values.senderEmailDomain || ''}`.trim()

    if (!values.campaignName?.trim()) {
      message.error('Please enter campaign name')
      return
    }

    if (!values.subjectLine?.trim()) {
      message.error('Please enter subject line')
      return
    }

    if (!values.senderEmail?.trim()) {
      message.error('Please enter sender email')
      return
    }

    if (!values.senderName?.trim()) {
      message.error('Please enter sender name')
      return
    }

    if (selectedRecipientIds.length === 0) {
      message.error('Select at least one recipient')
      return
    }

    if (!htmlContent || !htmlContent.trim()) {
      message.error('Email content cannot be empty')
      return
    }

    const payload = {
      campaign_name: values.campaignName.trim(),
      subject_line: values.subjectLine.trim(),
      sender_email: senderEmailFull,
      sender_name: values.senderName.trim(),
      reply_to_email: values.replyToEmail || '',
      add_to_calendar: addToCalendar,
      recipient_ids: selectedRecipientIds,
      recipient_filters: recipientFilters,
      html_content: htmlContent,
      design_json: designJson,
      attachment_signed_ids: attachments.map((file) => file.signedId),
      status: (initialEmail as { status?: string })?.status || 'draft',
    }

    emailBlastForm.setData('email_blast', payload)

    const url =
      formMode === 'edit'
        ? `/events/${event.slug}/messaging/email_blasts/${initialEmail?.id}`
        : `/events/${event.slug}/messaging/email_blasts`

    const submit = formMode === 'edit' ? emailBlastForm.patch : emailBlastForm.post

    submit(url, {
      preserveScroll: true,
      onSuccess: () => {
        message.success(
          formMode === 'create' ? 'Email created successfully' : 'Email updated successfully'
        )
      },
      onError: (formErrors) => {
        const firstError = formErrors && Object.values(formErrors)[0]
        const normalizedError = Array.isArray(firstError) ? firstError[0] : firstError
        message.error(normalizedError || 'Failed to save email.')
      },
    })
  }

  const handleCancel = () => {
    router.visit(`/events/${event?.slug}/messaging/email_blasts`)
  }

  const buildEmailPreviewData = (
    values: FormValues,
    overrides?: Partial<EmailPreviewData>
  ): EmailPreviewData => ({
    campaignName: values.campaignName || 'Untitled Campaign',
    subjectLine: values.subjectLine || 'No subject',
    senderName: values.senderName || 'Sender',
    senderEmail: `${values.senderEmail || 'no-reply'}${
      values.senderEmailDomain || '@avaeventhub.com'
    }`,
    recipientCount: selectedRecipientIds.length,
    htmlContent,
    updatedAt: new Date().toISOString(),
    ...overrides,
  })

  const getFormValues = (overrides?: Partial<EmailPreviewData>) => {
    const values = form.getFieldsValue() as FormValues
    return buildEmailPreviewData(values, overrides)
  }

  const getRecipientLabel = () => {
    if (recipientFilters.allAttendees) {
      return 'All Attendees'
    }
    if (selectedRecipientIds.length > 0) {
      return `${selectedRecipientIds.length} recipient${
        selectedRecipientIds.length > 1 ? 's' : ''
      } selected`
    }
    return null
  }

  return (
    <EventsLayout>
      <PageHeader
        title={formMode === 'create' ? 'Create Manual Email' : 'Edit Manual Email'}
        status="draft"
        breadcrumb={[
          {
            title: 'Email Blast',
            href: `/events/${event?.slug}/messaging/email_blasts`,
          },
          { title: formMode === 'create' ? 'Create' : 'Edit' },
        ]}
        showBackButton
        onBack={handleCancel}
      />

      <div className="flex gap-8 px-14 py-6">
        {/* Left Panel - Form */}
        <div className="w-[400px] flex-shrink-0">
          <Form
            size="small"
            form={form}
            layout="vertical"
            className="compact-form"
            initialValues={initialValues}
            onFinish={handleSubmit}
            onValuesChange={(_, values: FormValues) => {
              emailBlastForm.setData('email_blast', {
                ...(emailBlastForm.data.email_blast || {}),
                campaign_name: values.campaignName || '',
                subject_line: values.subjectLine || '',
                sender_email: `${values.senderEmail || ''}${values.senderEmailDomain || ''}`,
                sender_name: values.senderName || '',
                reply_to_email: values.replyToEmail || '',
              })
            }}
          >
            <Form.Item
              label="Campaign Name"
              name="campaignName"
              rules={[{ required: true, message: 'Please enter campaign name' }]}
            >
              <Input placeholder="Enter campaign name" size="small" />
            </Form.Item>

            <Form.Item
              label="Email Subject Line"
              name="subjectLine"
              rules={[{ required: true, message: 'Please enter subject line' }]}
            >
              <Input placeholder="Enter subject line" size="small" />
            </Form.Item>

            <Form.Item label="Select recipients">
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={getRecipientLabel() || ''}
                  placeholder="Select recipients"
                  size="small"
                  suffix={<HiOutlineUser className="text-gray-400" />}
                  onClick={handleSelectRecipients}
                  className="cursor-pointer"
                />
              </div>
              {getRecipientLabel() && (
                <div className="mt-2">
                  <Tag
                    closable
                    onClose={() => {
                      setSelectedRecipientIds([])
                      setRecipientFilters(defaultRecipientFilters)
                      emailBlastForm.setData('email_blast', {
                        ...(emailBlastForm.data.email_blast || {}),
                        recipient_ids: [],
                        recipient_filters: defaultRecipientFilters,
                      })
                    }}
                  >
                    {getRecipientLabel()}
                  </Tag>
                </div>
              )}
            </Form.Item>

            <Form.Item label="Sender Email" required>
              <Space.Compact className="w-full">
                <Form.Item
                  name="senderEmail"
                  noStyle
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input style={{ width: '40%' }} placeholder="Enter email" size="small" />
                </Form.Item>
                <Form.Item name="senderEmailDomain" noStyle>
                  <Select style={{ width: '60%' }} size="small" options={EMAIL_DOMAINS} />
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label="Sender Name"
              name="senderName"
              rules={[{ required: true, message: 'Please enter sender name' }]}
            >
              <Input placeholder="Enter sender name" size="small" />
            </Form.Item>

            <Form.Item label="Reply-to-Email" name="replyToEmail">
              <Input placeholder="Enter reply-to-email" size="small" />
            </Form.Item>

            <Form.Item label="Attachments">
              <EmailAttachmentsField
                eventSlug={event.slug}
                value={attachments}
                onChange={handleAttachmentsChange}
                limits={attachmentLimits}
                bodyBytes={htmlByteSize}
              />
            </Form.Item>
          </Form>
        </div>

        {/* Right Panel - Email Preview */}
        <div className="flex-1 bg-gray-50 rounded-lg p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium m-0">Email Editor</h3>
              <Space>
                <Button variant="secondary" onClick={() => setLoadContentModalOpen(true)}>
                  Load From Event
                </Button>
                <Button variant="primary" icon={<HiOutlinePencil />} onClick={handleEditEmail}>
                  Edit
                </Button>
              </Space>
            </div>
          </div>

          {/* Email Preview */}
          <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
            <iframe
              srcDoc={htmlContent}
              title="Email Preview"
              className="w-full h-[770px] border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>

      <FixedBottomActions
        leftActions={
          <Space>
            <Button variant="primary" onClick={() => form.submit()}>
              {formMode === 'create' ? 'Create Email' : 'Save Changes'}
            </Button>
            <Button variant="secondary" icon={<EyeOutlined />} onClick={handlePreview}>
              Preview
            </Button>
            <Button variant="secondary" icon={<SendOutlined />} onClick={handleSendTest}>
              Send Test Email
            </Button>
          </Space>
        }
        rightActions={
          <Button variant="tertiary" onClick={handleCancel}>
            Cancel
          </Button>
        }
      />

      {/* Recipient Selection Drawer */}
      <RecipientSelectionDrawer
        open={recipientDrawerOpen}
        recipients={availableRecipients}
        selectedRecipientIds={selectedRecipientIds}
        initialFilters={recipientFilters}
        filterOptions={filterOptions}
        onClose={() => setRecipientDrawerOpen(false)}
        onApply={handleApplyRecipients}
      />

      {/* Send Test Email Modal */}
      <SendTestEmailModal
        open={testEmailModalOpen}
        onClose={() => setTestEmailModalOpen(false)}
        onSend={handleSendTestEmails}
        loading={testSendLoading}
      />

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        email={getFormValues({
          htmlContent: previewHtml || htmlContent,
          subjectLine: previewSubject || form.getFieldValue('subjectLine') || 'No subject',
        })}
        mode={formMode === 'create' ? 'create' : 'view'}
        previewRecipient={previewRecipient || undefined}
        loading={previewLoading}
        onCreateEmail={() => {
          setPreviewModalOpen(false)
          form.submit()
        }}
        onSendTest={() => {
          setPreviewModalOpen(false)
          setTestEmailModalOpen(true)
        }}
      />

      {/* Email Editor Modal */}
      <EmailEditorModal
        open={editorModalOpen}
        onClose={() => setEditorModalOpen(false)}
        onSave={handleSaveEmailContent}
        initialHtml={htmlContent}
        initialDesignJson={designJson}
        mergeTagOptions={mergeTagOptions}
      />

      <LoadEmailContentModal
        open={loadContentModalOpen}
        eventSlug={event.slug}
        onClose={() => setLoadContentModalOpen(false)}
        onApply={handleLoadContentFromEvent}
      />
    </EventsLayout>
  )
}

export default NewEmailBlast
